import { Transferable } from "node:worker_threads";

/**
 * Checks if a value is a string containing
 * only alphabetic characters (a-z, case-insensitive)
 *
 * @param value - The value to check
 *
 * @returns `true` if the value is an alphabetic string, `false` otherwise
 */
export function isAlpha(value: unknown): value is string {
  return isString(value) && /^[a-z]+$/gi.test(value);
}

/**
 * Checks if a value is a string containing
 * only alphanumeric characters (a-z, 0-9, case-insensitive).
 *
 * @param value - The value to check
 *
 * @returns `true` if the value is an alphanumeric string, `false` otherwise
 */
export function isAlphaNumeric(value: unknown): value is string {
  return isString(value) && /^[a-z0-9]+$/gi.test(value);
}

/**
 * Checks if a value is an array
 *
 * @template T - The expected type of array elements (defaults to `unknown`).
 *
 * @param value - The value to check
 *
 * @returns `true` if the value is an array, `false` otherwise
 */
export function isArray<T>(value: unknown): value is T[] {
  return Array.isArray(value);
}

/**
 * Checks if a value is an `AsyncIterable`
 *
 * @param value - The value to check
 *
 * @returns `true` if the value is an `AsyncIterable`, `false` otherwise
 */
export function isAsyncIterable(
  value: unknown,
): value is AsyncIterable<unknown> {
  return (
    isObject(value) &&
    Symbol.asyncIterator in value &&
    isFunction(value[Symbol.asyncIterator])
  );
}

/**
 * Checks if a value is a `bigint`
 *
 * @param value - The value to check
 *
 * @returns `true` if the value is a `bigint`, `false` otherwise
 */
export function isBigInt(value: unknown): value is bigint {
  return typeof value === "bigint";
}

/**
 * Checks if a value is a `boolean`
 *
 * @param value - The value to check
 *
 * @returns `true` if the value is a `boolean`, `false` otherwise
 */
export function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

/**
 * Checks if a value is a function
 *
 * @param value - The value to check
 *
 * @returns `true` if the value is a function, `false` otherwise
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export function isFunction(value: unknown): value is Function {
  return typeof value === "function";
}

/**
 * Checks if a value is an integer number
 *
 * @param value - The value to check
 *
 * @returns `true` if the value is an integer, `false` otherwise.
 */
export function isInt(value: unknown): value is number {
  return isNumber(value) && Number.isInteger(value);
}

/**
 * Checks if a value is an `Iterable`
 *
 * @param value - The value to check
 *
 * @returns `true` if the value is an `Iterable`, `false` otherwise
 */
export function isIterable(value: unknown): value is Iterable<unknown> {
  return (
    isObject(value) &&
    Symbol.iterator in value &&
    isFunction(value[Symbol.iterator])
  );
}

/**
 * Checks if a value is a `number`
 *
 * @param value - The value to check
 *
 * @returns `true` if the value is a `number`, `false` otherwise
 */
export function isNumber(value: unknown): value is number {
  return typeof value === "number";
}

/**
 * Checks if a value is an `object`. Excludes `null` and arrays.
 *
 * @param value - The value to check
 *
 * @returns `true` if the value is an `object`, `false` otherwise
 */
export function isObject(value: unknown): value is object {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Checks if a value is a `string`
 *
 * @param value - The value to check
 *
 * @returns `true` if the value is a `string`, `false` otherwise
 */
export function isString(value: unknown): value is string {
  return typeof value === "string";
}

/**
 * Checks if a value is a `Transferable` object
 *
 * @param value - The value to check
 *
 * @returns `True` if the value is `Transferable`, `false` otherwise
 *
 * @todo Add a reliable check for `FileHandle`
 */
export function isTransferable(value: unknown): value is Transferable {
  return (
    value instanceof ArrayBuffer ||
    value instanceof MessagePort ||
    value instanceof AbortSignal ||
    value instanceof ReadableStream ||
    value instanceof WritableStream ||
    value instanceof TransformStream
  );
}
